const RECAPTCHA_SCRIPT_ID = 'gs-recaptcha-api'
const RECAPTCHA_SCRIPT_SRC = `https://www.google.com/recaptcha/api.js?render=${process.env.RECAPTCHA_ID}&ic=15&render=explicit`
const RECAPTCHA_LOAD_TIMEOUT_MS = 10000
const RECAPTCHA_RETRY_DELAYS_MS = [2000, 3000, 4000, 5000]

let recaptchaLoadPromise = null

export async function getRecaptchaV3Token(action = undefined) {
  await recaptchaReady()
  return window.grecaptcha.execute(process.env.RECAPTCHA_ID, { action })
}

export function warmupRecaptchaScript() {
  return loadRecaptchaScript().catch((error) => {
    console.error('[recaptcha] warmup failed', error)
  })
}

export async function getRecaptchaV2Token(element, options = {}) {
  await recaptchaReady()

  return new Promise((resolve) => {
    const widgetId = window.grecaptcha.render(element, {
      sitekey: process.env.RECAPTCHA_V2,
      theme: 'dark',
      callback: (token) => {
        window.grecaptcha.reset(widgetId)
        resolve(token)
      },
      ...options,
    })
  })
}

async function recaptchaReady() {
  await loadRecaptchaScript()

  return new Promise((resolve) => {
    window.grecaptcha.ready(resolve)
  })
}

function loadRecaptchaScript() {
  if (window.grecaptcha) {
    return Promise.resolve()
  }

  if (recaptchaLoadPromise) {
    return recaptchaLoadPromise
  }

  recaptchaLoadPromise = loadRecaptchaScriptWithRetry().catch((error) => {
    recaptchaLoadPromise = null
    throw error
  })

  return recaptchaLoadPromise
}

async function loadRecaptchaScriptWithRetry() {
  let lastError = null

  for (let attempt = 0; attempt <= RECAPTCHA_RETRY_DELAYS_MS.length; attempt += 1) {
    if (window.grecaptcha) {
      return
    }

    try {
      await loadRecaptchaScriptOnce()
      return
    } catch (error) {
      lastError = error

      if (!window.grecaptcha) {
        removeRecaptchaScript()
      }

      if (attempt === RECAPTCHA_RETRY_DELAYS_MS.length) {
        break
      }

      await wait(RECAPTCHA_RETRY_DELAYS_MS[attempt])
    }
  }

  throw lastError
}

function loadRecaptchaScriptOnce() {
  if (window.grecaptcha) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const existing = document.getElementById(RECAPTCHA_SCRIPT_ID)
    const script = existing || createRecaptchaScript()

    let timeoutId = null
    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
    }

    const onLoad = () => {
      waitForGrecaptcha()
        .then(() => {
          cleanup()
          resolve()
        })
        .catch((error) => {
          cleanup()
          reject(error)
        })
    }

    const onError = () => {
      cleanup()
      reject(new Error('Failed to load reCAPTCHA script'))
    }

    script.addEventListener('load', onLoad, { once: true })
    script.addEventListener('error', onError, { once: true })

    timeoutId = setTimeout(() => {
      cleanup()
      reject(new Error(`Timed out while waiting for reCAPTCHA (${RECAPTCHA_LOAD_TIMEOUT_MS}ms)`))
    }, RECAPTCHA_LOAD_TIMEOUT_MS)

    if (window.grecaptcha) {
      onLoad()
      return
    }

    if (!existing) {
      document.head.appendChild(script)
    }
  })
}

function createRecaptchaScript() {
  const script = document.createElement('script')
  script.id = RECAPTCHA_SCRIPT_ID
  script.src = RECAPTCHA_SCRIPT_SRC
  script.async = true
  script.defer = true
  return script
}

function removeRecaptchaScript() {
  document.getElementById(RECAPTCHA_SCRIPT_ID)?.remove()
}

function waitForGrecaptcha() {
  if (window.grecaptcha) {
    return Promise.resolve()
  }

  return window.watchGlobalVariable('grecaptcha')
}

function wait(delayMs) {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs)
  })
}
